import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Pin, CalendarDays, HandHeart, Mail, MapPin } from 'lucide-react';
import { boardPublicAPI, fmtWhen } from '@/lib/bulletinAPI';

// The church-facing notice board: notices, volunteer sign-ups and the newsletter. No login.
export const PublicShell = ({ title, logo, children }) => (
  <div className="min-h-screen" style={{ background: '#FBF8F2', color: '#1E2A33', fontFamily: 'Manrope, Arial, sans-serif' }}>
    <header className="border-b bg-white" style={{ borderColor: '#E3DDD1' }}>
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
        {logo && <img src={logo} alt="" className="w-10 h-10 rounded object-contain" />}
        <div className="text-xl md:text-2xl font-semibold leading-tight" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{title}</div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">{children}</main>
    <footer className="max-w-3xl mx-auto px-4 pb-8 text-xs text-[#5C6670]">Powered by Stagehead</footer>
  </div>
);

const linkify = (text) => (text || '').split(/(https?:\/\/[^\s]+)/g).map((part, i) => (
  /^https?:\/\//.test(part) ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#A62B1F] underline break-all">{part}</a> : part
));

const LABELS = { announcement: 'Announcement', event: 'Event', volunteer: 'Volunteers', prayer: 'Prayer', news: 'News', other: '' };

export const Subscribe = ({ slug }) => {
  const [f, setF] = useState({ name: '', email: '', website: '' });
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  if (state === 'done') return <p className="text-base">Thanks! Check your email and tap the link to confirm.</p>;
  return (
    <form className="space-y-2" onSubmit={async (e) => {
      e.preventDefault();
      setState('busy'); setMsg('');
      try { await boardPublicAPI.subscribe(slug, f); setState('done'); } catch (er) { setMsg(er?.response?.data?.detail || 'Something went wrong. Please try again.'); setState('idle'); }
    }}>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="h-12 rounded-lg border px-3 text-base bg-white" style={{ borderColor: '#E3DDD1' }} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input className="h-12 rounded-lg border px-3 text-base bg-white" style={{ borderColor: '#E3DDD1' }} placeholder="Email address" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      </div>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
      {msg && <p className="text-sm text-[#A62B1F]">{msg}</p>}
      <button disabled={state === 'busy'} className="h-12 px-6 rounded-lg text-white font-semibold text-base" style={{ background: '#A62B1F' }}>{state === 'busy' ? 'Sending…' : 'Subscribe'}</button>
      <p className="text-xs text-[#5C6670]">We'll email you a link to confirm. You can unsubscribe from any email.</p>
    </form>
  );
};

export default function PublicBoard() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { boardPublicAPI.board(slug).then((r) => { setData(r.data); document.title = r.data.title; }).catch(() => setMissing(true)); }, [slug]);

  if (missing) return <PublicShell title="Notice board"><div className="rounded-xl border bg-white p-6" style={{ borderColor: '#E3DDD1' }}>This notice board isn't available.</div></PublicShell>;
  if (!data) return <PublicShell title=""><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div></PublicShell>;

  return (
    <PublicShell title={data.title} logo={data.logo_url}>
      {data.intro && <p className="text-lg leading-relaxed whitespace-pre-line">{data.intro}</p>}

      {data.signups.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><HandHeart className="w-5 h-5 text-[#A62B1F]" />Volunteers needed</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.signups.map((ev) => (
              <Link key={ev.id} to={`/board/${slug}/signup/${ev.id}`} className="block rounded-xl border bg-white p-4 hover:shadow-sm" style={{ borderColor: '#E3DDD1' }}>
                <div className="font-semibold text-base">{ev.title}</div>
                <div className="text-sm text-[#5C6670] mt-1">{fmtWhen(ev.starts_at, { hour: undefined, minute: undefined })}{ev.location ? ` · ${ev.location}` : ''}</div>
                <div className="text-sm mt-2 font-semibold" style={{ color: ev.open_spots ? '#A62B1F' : '#5C6670' }}>{ev.open_spots ? `${ev.open_spots} spot${ev.open_spots === 1 ? '' : 's'} open · Sign up →` : 'All spots filled'}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        {data.posts.length === 0 && <p className="text-[#5C6670]">No notices right now. Check back soon.</p>}
        {data.posts.map((p) => (
          <article key={p.id} className="rounded-xl border bg-white p-5" style={{ borderColor: p.pinned ? '#A62B1F' : '#E3DDD1' }}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-[#A62B1F] mb-1">
              {p.pinned && <Pin className="w-3.5 h-3.5" />}{LABELS[p.category]}
            </div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{p.title}</h3>
            {p.event_date && <div className="text-sm font-semibold mt-1 flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtWhen(p.event_date, { weekday: 'long', month: 'long' })}</div>}
            {p.body && <div className="text-base leading-relaxed mt-2 whitespace-pre-line">{linkify(p.body)}</div>}
          </article>
        ))}
      </section>

      <section className="rounded-xl border bg-white p-5" style={{ borderColor: '#E3DDD1' }}>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Mail className="w-5 h-5 text-[#A62B1F]" />Get the newsletter</h2>
        <p className="text-sm text-[#5C6670] mb-3">News and notices from {data.company}, by email.</p>
        <Subscribe slug={slug} />
      </section>
    </PublicShell>
  );
}
